<template>
  <v-dialog v-if="user" persistent width="560" max-width="100%" :value="show">
    <v-card light class="ps-dialog">
      <v-card-title v-if="!receivedPassword">
        <div>
          <div class="headline">{{ editedUser.username || "Benutzer" }}</div>
          <div class="text-caption text--secondary">Benutzer bearbeiten</div>
        </div>
      </v-card-title>
      <v-card-text>
        <v-form v-if="!receivedPassword" v-model="validForm">
          <section class="ps-dialog-section">
            <div class="ps-dialog-section__head">
              <v-icon small class="mr-2">mdi-account-outline</v-icon>Allgemein
            </div>
            <v-row dense>
              <v-col cols="12">
                <v-text-field
                  label="Benutzername"
                  outlined dense hide-details="auto"
                  v-model="editedUser.username"
                  :rules="usernameRules"
                  required
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  disabled
                  label="E-Mail"
                  outlined dense hide-details="auto"
                  v-model="editedUser.email"
                  :rules="emailRules"
                />
              </v-col>
            </v-row>
          </section>

          <section v-if="editedUser.payment" class="ps-dialog-section">
            <div class="ps-dialog-section__head">
              <v-icon small class="mr-2">mdi-cash-multiple</v-icon>Bezahlung
            </div>
            <v-row dense>
              <v-col cols="6">
                <v-text-field
                  label="Vergütung pro Task"
                  outlined dense hide-details
                  type="number"
                  v-model="editedUser.payment.payPerTask"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  label="Währung (z. B. €)"
                  outlined dense hide-details
                  v-model="editedUser.payment.currency"
                  :disabled="!editedUser.payment.payPerTask"
                />
              </v-col>
            </v-row>
          </section>

          <section class="ps-dialog-section">
            <div class="ps-dialog-section__head">
              <v-icon small class="mr-2">mdi-shield-account-outline</v-icon>Berechtigungen &amp; Zuordnung
            </div>
            <v-row dense>
              <v-col v-if="availablePermissions.length > 1" cols="12">
                <v-combobox
                  v-model="editedUser.permissions"
                  :items="availablePermissions"
                  label="Berechtigungen"
                  multiple
                  outlined dense hide-details
                  small-chips
                ></v-combobox>
              </v-col>
              <v-col cols="12">
                <v-autocomplete
                  v-model="editedUser.assignedProductTypes"
                  :items="availableProductTypes"
                  label="Zugewiesene Product Types"
                  multiple
                  item-text="name"
                  item-value="key"
                  outlined dense hide-details
                  small-chips
                ></v-autocomplete>
              </v-col>
              <v-col cols="12">
                <v-switch
                  dense inset hide-details class="mt-1"
                  v-model="editedUser.needsReview"
                  label="Ergebnisse müssen reviewt werden"
                ></v-switch>
              </v-col>
            </v-row>
          </section>
        </v-form>
        <div v-else class="showPassword">Benutzer aktualisiert!</div>
      </v-card-text>
      <v-card-actions class="px-6 pb-5">
        <v-btn @click="close" text rounded>Abbrechen</v-btn>
        <v-spacer />
        <v-btn
          :disabled="!hasChanges || isUpdating"
          :loading="isUpdating"
          color="primary" depressed rounded
          @click="updateUser"
          v-if="!receivedPassword"
        >Speichern</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { permissions } from "@/constants";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../../fb";
import { mapGetters } from "vuex";
import { EventBus, events } from "../../../../event-bus";

const _ = require("lodash");

export default {
  name: "EditUserDialog",
  props: {
    show: {
      default: false,
    },
    closeDialog: {
      required: true,
    },
    user: {
      required: true,
    },
  },
  data() {
    return {
      editedUser: {
        permissions: [],
        username: "",
        needsReview: true,
        payment: {
          transactionHistory: [],
          unpaidTasks: [],
          currency: "",
          payPerTask: 1,
        },
        email: "",
      },
      showDialog: false,
      isUpdating: false,
      receivedPassword: "",
      usernameRules: [
        (v) => !!v || "Name erforderlich",
        (v) => v.length <= 10 || "Maximal 10 Zeichen",
        (v) => v.length >= 3 || "Mindestens 3 Zeichen",
      ],
      emailRules: [
        (v) => !!v || "E-Mail erforderlich",
        (v) => /.+@.+/.test(v) || "E-Mail ungültig",
      ],
      validForm: true,
    };
  },
  watch: {
    user: {
      deep: true,
      handler: function (newVal) {
        if (newVal) {
          this.editedUser = _.cloneDeep(newVal);
        }
      },
    },
  },
  computed: {
    ...mapGetters(["userPermissions", "productTypes"]),
    availablePermissions() {
      return this.userPermissions.filter((permission) => {
        if (this.userPermissions.includes(permissions.ADMIN)) return true;
        return (
          permission !== permissions.MANAGER &&
          permission !== permissions.TASK_REVIEW
        );
      });
    },
    availableProductTypes() {
      return this.productTypes.map(productType => {
        return {
          name: productType.name,
          key: productType.key,
        }
      })
    },
    hasChanges() {
      return !_.isEqual(this.user, this.editedUser);
    },
  },
  methods: {
    close() {
      this.closeDialog();
      // Wait for closing animation
      setTimeout(() => {
        this.editedUser = _.cloneDeep(this.user);
      }, 500);
    },
    async updateUser() {
      if (!this.validForm) {
        EventBus.$emit(events.SNACKBAR, {
          message: "Bitte alle Pflichtfelder ausfüllen",
          type: "error",
        });
      } else {
        this.isUpdating = true;
        const updateUser = httpsCallable(functions, "updateUser");
        const response = await updateUser({
          uid: this.editedUser.uid,
          username: this.editedUser.username,
          payPerTask: this.editedUser.payment?.payPerTask,
          currency: this.editedUser.payment?.currency,
          permissions: this.editedUser.permissions,
          needsReview: this.editedUser.needsReview,
          assignedProductTypes: this.editedUser.assignedProductTypes,
        });
        this.isUpdating = false;

        if (response.data.success) {
          EventBus.$emit(events.SNACKBAR, {
            message: this.editedUser.username + " erfolgreich aktualisiert",
          });
          this.close();
        } else {
          EventBus.$emit(events.SNACKBAR, {
            type: "error",
            message: response.data.errorMessage,
          });
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.showPassword {
  height: 100px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}
</style>
